import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Plus, X, Send, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const MAX = 1700;

const toJpeg = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const r = Math.min(1, MAX / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * r); c.height = Math.round(img.height * r);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(url);
    resolve({ data: c.toDataURL("image/jpeg", 0.82), w: c.width, h: c.height });
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Photo illisible")); };
  img.src = url;
});

async function buildPdf(pages) {
  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  pages.forEach((p, i) => {
    if (i) pdf.addPage();
    const r = Math.min(200 / p.w, 287 / p.h);
    pdf.addImage(p.data, "JPEG", (210 - p.w * r) / 2, (297 - p.h * r) / 2, p.w * r, p.h * r);
  });
  return new File([pdf.output("blob")], `scan_facture_${new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-")}.pdf`, { type: "application/pdf" });
}

export function ScanDialog({ open, onOpenChange, onSend }) {
  const [pages, setPages] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const add = async (files) => {
    try { const got = await Promise.all(Array.from(files || []).map(toJpeg)); setPages((p) => [...p, ...got]); }
    catch (e) { toast.error(e.message); }
    if (ref.current) ref.current.value = "";
  };
  const close = (o) => { if (!busy) { onOpenChange(o); if (!o) setPages([]); } };
  const send = async () => {
    setBusy(true);
    try { const f = await buildPdf(pages); if (f.size > 15 * 1024 * 1024) throw new Error("Scan trop lourd (15 Mo max), retirez une page"); await onSend(f); setPages([]); onOpenChange(false); }
    catch (e) { toast.error(e.message || "Échec du scan"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg" data-testid="scan-dialog">
        <DialogTitle className="font-display text-xl">Scanner une facture</DialogTitle>
        <p className="text-sm text-gray-500">Photographiez chaque page à plat, bien éclairée. Les pages sont réunies en un seul PDF puis lues automatiquement.</p>
        <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => add(e.target.files)} data-testid="scan-camera-input" />
        <div className="grid grid-cols-3 gap-2" data-testid="scan-pages">
          {pages.map((p, i) => (
            <div key={i} className="relative overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
              <img src={p.data} alt={`Page ${i + 1}`} className="aspect-[3/4] w-full object-cover" />
              <span className="absolute bottom-1 left-1 rounded-full bg-white/90 px-1.5 text-[10px] font-semibold">p. {i + 1}</span>
              <button type="button" aria-label="Retirer la page" onClick={() => setPages(pages.filter((_, j) => j !== i))} data-testid={`scan-remove-${i}`}
                className="absolute right-1 top-1 rounded-full bg-white/90 p-0.5 text-gray-600 hover:text-rose-600"><X className="h-3.5 w-3.5" /></button>
            </div>))}
          <button type="button" onClick={() => ref.current?.click()} data-testid="scan-add-page"
            className="flex aspect-[3/4] flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-emerald-200 bg-emerald-50/40 text-xs font-semibold text-[#065F46] transition-colors hover:bg-emerald-50">
            {pages.length ? <Plus className="h-6 w-6" /> : <Camera className="h-6 w-6" />}{pages.length ? "Ajouter une page" : "Prendre la photo"}
          </button>
        </div>
        <Button onClick={send} disabled={!pages.length || busy} className="rounded-full bg-[#065F46] hover:bg-[#054d39]" data-testid="scan-send">
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}Envoyer {pages.length ? `(${pages.length} page${pages.length > 1 ? "s" : ""})` : ""}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
